import { JMCHtml } from "@components/JMCHtml/JMCHtml";
import { useBottomContent } from "@components/Layout/LogicSpacing/LogicSpacing";
import { SectionImpression } from "@components/SectionImpression/SectionImpression";
import { FootnotesContext } from "@contexts";
import anchorScroll from "@jmc/core/src/utils/anchor-scroll";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Color } from "@jmc/solid-design-system/src/components/molecules/HeaderHero/HeaderHero";
import useJnjBranding from "@jmc/utils/hooks/useJnjBranding";
import { update } from "@redux/modules/bottomContent";
import { CMSFootnotes, Footnote } from "@types";
import classnames from "classnames";
import React, { Dispatch, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import style from "./style.module.scss";

type PropTypes = {
    footnotes: CMSFootnotes[];
};

/**
 * FootnotesSection is a component that renders a list of footnotes that link back to the corresponding content on the page.
 */
const FootnotesSectionForExport = ({ footnotes }: PropTypes): JSX.Element => {
    const { t } = useTranslation();
    // Forces a rerender whenever the JMCHtml component parses text which contains footnotes
    // This is a workaround for elements which load later and are not parents of the footnotes section in the DOM tree (Page Header, Carousels etc)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ = useContext(FootnotesContext);
    const [hasFootnotesChecked, setHasFootnotesChecked] = useState(false);
    const [headerHeight, setHeaderHeight] = useState(0);
    const { jnjFullBranded } = useJnjBranding();
    const getFootnoteElements = (footnoteIndex: number): HTMLCollection | null => {
        return typeof window !== "undefined" && document?.getElementsByClassName(`footnote-${footnoteIndex}-text`);
    };

    const { lastSectionBackground } = useBottomContent();

    let dispatch: Dispatch<unknown>;

    if (jnjFullBranded) {
        dispatch = useDispatch();
    }

    const getFirstVisibleFootnote = (footnoteIndex: number): HTMLElement | null => {
        if (typeof window !== "undefined") {
            const footnoteElementList = document?.getElementsByClassName(`footnote-${footnoteIndex}-text`);
            // Select first visible element in the DOM
            for (const footnote of footnoteElementList) {
                if (footnote.checkVisibility()) return footnote as HTMLElement;
            }
        }
        return null;
    };
    const hasFootnotes = (): boolean => {
        return footnotes.reduce((acc, current) => {
            if (current.footnotes.find((f) => f.index > 0 && getFootnoteElements(f.index).length > 0)) {
                acc = true;
            }
            return acc;
        }, false as boolean);
    };

    const ordered: Footnote[] = [];

    useEffect(() => {
        const hasVisibleFootnotes = hasFootnotes();
        setHasFootnotesChecked(hasVisibleFootnotes);
        if (jnjFullBranded) {
            dispatch(update({ hasVisibleFootnotes }));
        }
    });

    useEffect(() => {
        setHeaderHeight(document.getElementById("main-header")?.offsetHeight ?? 0);
    }, []);

    if (!hasFootnotesChecked) {
        return null;
    }

    footnotes.map((f) =>
        f.footnotes.map((footnote: Footnote) => {
            if (!footnote.index) {
                return;
            }
            ordered.push(footnote);
        }),
    );

    ordered.sort((a: Footnote, b: Footnote) => {
        if (a.index < b.index) {
            return -1;
        }
        if (a.index > b.index) {
            return 1;
        }
        return 0;
    });

    const hasTopSpacing = lastSectionBackground && lastSectionBackground !== Color.WHITE;

    return jnjFullBranded ? (
        <SectionImpression name={"References"} position={1}>
            <div className={classnames(style.wrapper, hasTopSpacing && style["top-spacing"])}>
                <Typography variant="h2" weight="400">
                    {t("References", { ns: "common" })}
                </Typography>
                <div className={style["reference-list"]}>
                    {ordered.map((footnote: Footnote) => {
                        if (getFootnoteElements(footnote.index).length == 0) {
                            return;
                        }
                        const rteParsing = footnote.text.replace(/&lt;/g, "<").replace(/&gt;/g, ">");
                        return (
                            <div
                                key={footnote.text}
                                className={style["reference-row"]}
                                id={`footnote-${footnote.index}`}
                                data-test-id={`footnote-${footnote.index}`}
                                style={{ scrollMarginTop: `${headerHeight}px` }}
                            >
                                <Typography
                                    link
                                    size="s"
                                    onClick={(): void => {
                                        document.dispatchEvent(
                                            new CustomEvent("recalc", {
                                                detail: { index: footnote.index, isFootnote: true },
                                            }),
                                        );

                                        setTimeout(() => {
                                            const firstVisibleFootnote = getFirstVisibleFootnote(footnote.index);
                                            if (firstVisibleFootnote) {
                                                anchorScroll(firstVisibleFootnote, null, true);
                                            }
                                        }, 250);
                                    }}
                                    data-test-id={`footnote-${footnote.index}-index`}
                                    className={style["reference-number"]}
                                >
                                    [{footnote.index}]
                                </Typography>
                                <Typography
                                    size="s"
                                    color="gray"
                                    data-test-id={`footnote-${footnote.index}-text`}
                                    className={style["reference-description"]}
                                >
                                    <JMCHtml>{rteParsing}</JMCHtml>
                                </Typography>
                            </div>
                        );
                    })}
                </div>
            </div>
        </SectionImpression>
    ) : (
        <SectionImpression name={"References"} position={1}>
            <div className={style.element}>
                <Typography variant="h4" color="secondary" weight="600">
                    {t("References", { ns: "common" })}
                </Typography>
                {ordered.map((footnote: Footnote) => {
                    if (getFootnoteElements(footnote.index).length == 0) {
                        return;
                    }
                    const rteParsing = footnote.text.replace(/&lt;/g, "<").replace(/&gt;/g, ">");
                    return (
                        <div
                            key={footnote.text}
                            className={style.footnote}
                            id={`footnote-${footnote.index}`}
                            data-test-id={`footnote-${footnote.index}`}
                            style={{ scrollMarginTop: `${headerHeight}px` }}
                        >
                            <Typography
                                link
                                size="s"
                                onClick={(): void => {
                                    document.dispatchEvent(
                                        new CustomEvent("recalc", {
                                            detail: { index: footnote.index, isFootnote: true },
                                        }),
                                    );

                                    setTimeout(() => {
                                        const firstVisibleFootnote = getFirstVisibleFootnote(footnote.index);
                                        if (firstVisibleFootnote) {
                                            anchorScroll(firstVisibleFootnote, null, true);
                                        }
                                    }, 250);
                                }}
                                data-test-id={`footnote-${footnote.index}-index`}
                            >
                                [{footnote.index}]
                            </Typography>
                            <Typography size="s" color="gray" data-test-id={`footnote-${footnote.index}-text`}>
                                <JMCHtml>{rteParsing}</JMCHtml>
                            </Typography>
                        </div>
                    );
                })}
            </div>
        </SectionImpression>
    );
};

export const FootnotesSection = FootnotesSectionForExport;
